import type { CardReadResponse } from '@league/shared/contract';
import { useEffect, useRef, useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';

export type CardReading = Extract<CardReadResponse, { rotate: 0 }>;

/**
 * "READ THE CARD FROM A PHOTO" — owner, 2026-10-03.
 *
 * She photographs East's card; the grid below fills in as an unsaved draft and she checks it against
 * the card before saving. Nothing here saves anything, and the photo goes nowhere but the one
 * request that reads it.
 *
 * TWO THINGS HAPPEN TO THE PHOTO IN THE BROWSER FIRST:
 *
 * 1. It is SHRUNK to 1600px on the long side. A phone camera takes 12 megapixels; the model reads
 *    a card perfectly well at a fifth of that, the upload is a few hundred KB instead of several
 *    MB on a hall's bad signal, and Bedrock refuses anything over 3.75 MB anyway.
 * 2. It is TURNED when the server says it is not upright. In the trial every scan was sideways or
 *    upside down, and sideways is where the reading went wrong. The model can tell sideways from
 *    upright reliably but not which sideways — so a quarter turn may come back "now it is upside
 *    down", and the second answer is always right. Three tries is the most it can take.
 */
export function ReadCard({
  endpoint,
  tableNumber,
  replacing,
  onRead,
}: {
  /** Where the photo goes — the director's table route, or the player's own card. */
  endpoint: string;
  tableNumber: number;
  /** Something is already in the grid for this table, so reading will replace it. */
  replacing: boolean;
  onRead: (reading: CardReading) => void;
}) {
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /** The photo, ready to send — kept after a failure so "Try again" does not mean another photo. */
  const [prepared, setPrepared] = useState<HTMLCanvasElement | null>(null);

  async function read(file: File) {
    setError(null);
    setBusy('Reading the card…');
    let canvas: HTMLCanvasElement;
    try {
      canvas = await shrink(file);
    } catch {
      setBusy(null);
      setError('That photo could not be opened. Try taking it again.');
      return;
    } finally {
      if (input.current) input.current.value = '';
    }
    await readPrepared(canvas);
  }

  async function readPrepared(start: HTMLCanvasElement) {
    setError(null);
    setPrepared(null);
    setBusy('Reading the card…');
    let canvas = start;
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const result = await send(endpoint, await toJpeg(canvas), () => setBusy('Weak connection. Still trying…'));
        if (result.rotate === 0) {
          if (active.current) onRead(result);
          return;
        }
        setBusy('Turning the photo the right way up…');
        canvas = turn(canvas, result.rotate);
      }
      throw new Error('The photo could not be turned the right way up. Try taking it again, straight on.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The card could not be read. Type the scores in instead.');
      if (err instanceof ConnectionError) setPrepared(canvas);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex max-w-[34rem] flex-col gap-2">
      {/*
        `capture` opens the camera straight away on a phone; a laptop ignores it and offers a file,
        which is right for a director typing up cards someone photographed for her.
      */}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void read(file);
        }}
      />
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-light bg-sand px-3 py-2">
          <p className="text-[14px] text-ink-2">This replaces what is on screen for table {tableNumber}.</p>
          <Button
            variant="secondary"
            className="min-h-[44px]"
            onClick={() => {
              setConfirming(false);
              input.current?.click();
            }}
          >
            Take the photo
          </Button>
          <Button variant="secondary" className="min-h-[44px]" onClick={() => setConfirming(false)}>
            Keep what is here
          </Button>
        </div>
      ) : (
        <Button
          variant="secondary"
          className="min-h-[48px] w-full"
          disabled={busy !== null}
          onClick={() => (replacing ? setConfirming(true) : input.current?.click())}
        >
          {busy ?? 'Read the card from a photo'}
        </Button>
      )}
      {busy ? (
        <p role="status" className="text-[13px] text-ink-2">
          This takes about fifteen seconds.
        </p>
      ) : null}
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {prepared && !busy ? (
        <Button variant="secondary" className="min-h-[44px] self-start" onClick={() => void readPrepared(prepared)}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** The connection failed, not the reading — worth trying the same photo again. */
class ConnectionError extends Error {}

/** A card takes ten to fifteen seconds and the server gives up at sixty; past this, nothing is coming. */
const ATTEMPT_TIMEOUT_MS = 90_000;
/** Waits before the second and third tries when the connection drops. */
const RETRY_DELAYS_MS = [2_000, 5_000];

/**
 * ⚠ ONE PHOTO, UP TO THREE TRIES — owner, 2026-10-14: reading "doesn't work all the time,
 * especially when there is a weaker wifi connection or cellular". The send had no timeout, so a
 * dropped connection left "Reading the card…" on screen indefinitely, and a lost connection showed
 * the browser's own words ("Load failed"). Reading writes nothing, so sending it again is safe.
 */
async function send(endpoint: string, photo: Blob, onRetry: () => void): Promise<CardReadResponse> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await sendOnce(endpoint, photo);
    } catch (err) {
      const delay = RETRY_DELAYS_MS[attempt];
      if (!(err instanceof ConnectionError) || delay === undefined) throw err;
      onRetry();
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

async function sendOnce(endpoint: string, photo: Blob): Promise<CardReadResponse> {
  const form = new FormData();
  form.append('photo', photo, 'card.jpg');
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      body: form,
      signal: AbortSignal.timeout(ATTEMPT_TIMEOUT_MS),
    });
  } catch {
    throw new ConnectionError('The connection dropped while the card was being read. Try again, or type the scores in.');
  }
  const body = (await response.json().catch(() => null)) as (CardReadResponse & { message?: string }) | null;
  /* A proxy that gave up answers 502–504 with no message of ours — the connection, not the card. */
  if (response.status >= 502 && response.status <= 504 && !body?.message) {
    throw new ConnectionError('The connection dropped while the card was being read. Try again, or type the scores in.');
  }
  if (response.status === 429) throw new Error('Too many cards have been read in the last hour. Type the scores in for now.');
  if (!response.ok || !body) throw new Error(body?.message ?? 'The card could not be read. Type the scores in instead.');
  return body;
}

const LONG_EDGE = 1600;

/** Decodes the photo the right way round for the CAMERA (EXIF), then scales it down. */
async function shrink(file: File): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, LONG_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas;
}

/** A new canvas holding the old one turned `degrees` clockwise. */
function turn(source: HTMLCanvasElement, degrees: 90 | 180 | 270): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const sideways = degrees !== 180;
  canvas.width = sideways ? source.height : source.width;
  canvas.height = sideways ? source.width : source.height;
  const context = canvas.getContext('2d');
  if (!context) return source;
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((degrees * Math.PI) / 180);
  context.drawImage(source, -source.width / 2, -source.height / 2);
  return canvas;
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The photo could not be prepared.'))), 'image/jpeg', 0.85),
  );
}
